import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import { 
  Star, 
  TrendingUp, 
  Package, 
  AlertCircle, 
  ChevronLeft,
  Calendar,
  Leaf,
  BarChart3,
  Users,
  Award,
  Sparkles,
  Sun,
  Moon,
  Zap
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { getProductById, getBatchesByProduct, getApprovedReviews } from "../lib/api";
import type { ProductWithStats, BatchWithProduct, ReviewWithRelations } from "../lib/types";
import { toast } from "sonner";
import { getProductImage } from "../lib/productImageUtils";

interface ProductDetailProps {
  productId?: string;
  onNavigate?: (page: string, id?: string) => void;
}

export function ProductDetail({ productId, onNavigate }: ProductDetailProps) {
  const [product, setProduct] = useState<ProductWithStats | null>(null);
  const [batches, setBatches] = useState<BatchWithProduct[]>([]);
  const [reviews, setReviews] = useState<ReviewWithRelations[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState("overview");
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);

  useEffect(() => {
    const fetchProductData = async () => {
      if (!productId) {
        // Keep loading until we get a productId
        return;
      }

      try {
        setLoading(true);
        setHasAttemptedFetch(true);
        const [productData, batchesData, reviewsData] = await Promise.all([
          getProductById(productId),
          getBatchesByProduct(productId),
          getApprovedReviews({ productId }),
        ]);

        if (!productData) {
          toast.error("Product not found");
          onNavigate && onNavigate("products");
          return;
        }

        setProduct(productData);
        setBatches(batchesData);
        setReviews(reviewsData);
      } catch (error: any) {
        console.error("Error fetching product:", error);
        toast.error("Failed to load product details");
      } finally {
        setLoading(false);
      }
    };

    fetchProductData();
  }, [productId, onNavigate]);

  const getGradeFromStars = (stars: number): string => {
    if (stars >= 4.5) return "A+";
    if (stars >= 4.0) return "A";
    if (stars >= 3.5) return "B+";
    if (stars >= 3.0) return "B";
    if (stars >= 2.5) return "C+";
    if (stars >= 2.0) return "C";
    return "D";
  };

  const getGradeColor = (grade: string) => {
    if (grade.startsWith("A")) return "text-primary";
    if (grade.startsWith("B")) return "text-blue-600";
    if (grade.startsWith("C")) return "text-yellow-600";
    return "text-muted-foreground";
  };

  const getGradientClass = (grade: string) => {
    if (grade.startsWith("A")) return "gradient-grade-a";
    if (grade.startsWith("B")) return "gradient-grade-b";
    if (grade.startsWith("C")) return "gradient-grade-c";
    return "bg-muted";
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8 gradient-mesh">
        <div className="max-w-7xl mx-auto">
          <Skeleton className="h-10 w-40 mb-6" />
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <Skeleton className="h-96 w-full rounded-2xl" />
              <Skeleton className="h-64 w-full rounded-2xl" />
            </div>
            <div className="space-y-6">
              <Skeleton className="h-64 w-full rounded-2xl" />
              <Skeleton className="h-48 w-full rounded-2xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8 gradient-mesh">
        <div className="max-w-7xl mx-auto">
          <Card className="p-12 glass-card text-center">
            <Package className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-2xl mb-2">Product Not Found</h2>
            <p className="text-muted-foreground mb-6">
              We couldn't find the product you're looking for.
            </p>
            <Button 
              onClick={() => onNavigate && onNavigate("products")}
              className="gradient-green hover:opacity-90 transition-opacity"
            >
              Browse Products
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  const reviewCount = product.rating_stats?.[0]?.review_count || 0;
  const avgStars = product.rating_stats?.[0]?.avg_stars || 0;
  const overallGrade = getGradeFromStars(avgStars);
  const hasTopRatedBadge = product.badge?.[0]?.qualifies_for_badge || false;

  // Calculate grade breakdown from reviews
  const gradeBreakdown = {
    "A": reviews.filter(r => getGradeFromStars(r.stars).startsWith("A")).length,
    "B": reviews.filter(r => getGradeFromStars(r.stars).startsWith("B")).length,
    "C": reviews.filter(r => getGradeFromStars(r.stars).startsWith("C")).length,
    "D": reviews.filter(r => getGradeFromStars(r.stars).startsWith("D")).length,
    "F": reviews.filter(r => r.stars === 1).length,
  };

  const totalReviews = reviews.length;

  // Calculate star distribution
  const starDistribution = {
    5: reviews.filter(r => r.stars === 5).length,
    4: reviews.filter(r => r.stars === 4).length,
    3: reviews.filter(r => r.stars === 3).length,
    2: reviews.filter(r => r.stars === 2).length,
    1: reviews.filter(r => r.stars === 1).length,
  };

  // Calculate detailed rating averages
  const calculateAverage = (field: keyof ReviewWithRelations) => {
    const validReviews = reviews.filter(r => r[field] !== null && r[field] !== undefined);
    if (validReviews.length === 0) return null;
    const sum = validReviews.reduce((acc, r) => acc + (r[field] as number), 0);
    return sum / validReviews.length;
  };

  const detailedRatings = {
    scent: calculateAverage('scent_score') ?? calculateAverage('scent_rating'),
    dryness: calculateAverage('dryness_score') ?? calculateAverage('dryness_rating'),
    taste: calculateAverage('taste_score') ?? calculateAverage('taste_rating'),
    appearance: calculateAverage('appearance_score') ?? calculateAverage('appearance_rating'),
    packaging: calculateAverage('packaging_score') ?? calculateAverage('packaging_rating'),
  };

  // Calculate best used preferences
  const bestUsedCounts = {
    day: reviews.filter(r => r.best_used === 'day').length,
    night: reviews.filter(r => r.best_used === 'night').length,
    both: reviews.filter(r => r.best_used === 'both').length,
  };

  // Calculate repeat purchase rate
  const repeatPurchaseReviews = reviews.filter(r => r.repeat_purchase !== null);
  const repeatPurchaseRate = repeatPurchaseReviews.length > 0
    ? (reviews.filter(r => r.repeat_purchase === true).length / repeatPurchaseReviews.length) * 100
    : null;

  // Helper function to get rating color
  const getRatingColor = (rating: number | null) => {
    if (rating === null) return 'text-muted-foreground';
    if (rating >= 4.0) return 'text-primary';
    if (rating >= 3.0) return 'text-foreground';
    return 'text-red-600';
  };

  return (
    <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8 gradient-mesh">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Button 
            variant="ghost" 
            className="mb-6 hover:bg-primary/10"
            onClick={() => onNavigate && onNavigate("discover")}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to Discover
          </Button>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Product Header Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Card className="p-8 glass-card">
                <div className="flex flex-col md:flex-row gap-6">
                  {/* Product Image */}
                  <div className="w-full md:w-64 h-64 rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {getProductImage(product.id) ? (
                      <img 
                        src={getProductImage(product.id)!} 
                        alt={product.product_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-8xl opacity-30">🌿</div>
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="flex-1">
                    <div className="flex flex-wrap gap-2 mb-4">
                      <Badge className="capitalize">{product.form}</Badge>
                      {hasTopRatedBadge && (
                        <Badge className="gradient-green text-white">
                          <Sparkles className="w-3 h-3 mr-1" />
                          Top Rated
                        </Badge>
                      )}
                      {product.active && (
                        <Badge variant="outline" className="border-primary text-primary">
                          Active
                        </Badge>
                      )}
                    </div>

                    <h1 className="text-3xl mb-2">{product.product_name}</h1>
                    <p className="text-lg text-muted-foreground mb-1">{product.brand || "Various Brands"}</p>
                    <p className="text-sm text-muted-foreground mb-6">SKU: {product.sku}</p>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 rounded-xl bg-muted/30 text-center">
                        <p className="text-xs text-muted-foreground mb-1">THC</p>
                        <p className="text-lg font-medium">{product.thc_pct ? `${product.thc_pct}%` : "N/A"}</p>
                      </div>
                      <div className="p-4 rounded-xl bg-muted/30 text-center">
                        <p className="text-xs text-muted-foreground mb-1">CBD</p>
                        <p className="text-lg font-medium">{product.cbd_pct ? `${product.cbd_pct}%` : "N/A"}</p>
                      </div>
                      <div className="p-4 rounded-xl bg-muted/30 text-center">
                        <p className="text-xs text-muted-foreground mb-1">Pack Size</p>
                        <p className="text-sm font-medium">{product.pack_size || "N/A"}</p>
                      </div>
                      <div className="p-4 rounded-xl bg-muted/30 text-center">
                        <p className="text-xs text-muted-foreground mb-1">Batches</p>
                        <p className="text-lg font-medium">{batches.length}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>

            {/* Tabs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.5 }}
            >
              <Tabs value={selectedTab} onValueChange={setSelectedTab}>
                <TabsList className="grid w-full grid-cols-3 glass-card">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="batches">Batches ({batches.length})</TabsTrigger>
                  <TabsTrigger value="reviews">Reviews ({reviewCount})</TabsTrigger>
                </TabsList>

                {/* Overview Tab */}
                <TabsContent value="overview" className="space-y-6">
                  {/* Grade Distribution */}
                  <Card className="p-6 glass-card">
                    <div className="flex items-center gap-2 mb-6">
                      <BarChart3 className="w-5 h-5 text-primary" />
                      <h3 className="text-lg">Grade Distribution</h3>
                    </div>

                    <div className="space-y-4">
                      {Object.entries(gradeBreakdown).map(([grade, count]) => (
                        <div key={grade} className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className={`font-medium ${getGradeColor(grade)}`}>
                              Grade {grade}
                            </span>
                            <span className="text-muted-foreground">
                              {count} reviews ({totalReviews > 0 ? Math.round((count / totalReviews) * 100) : 0}%)
                            </span>
                          </div>
                          <Progress 
                            value={totalReviews > 0 ? (count / totalReviews) * 100 : 0} 
                            className="h-2"
                          />
                        </div>
                      ))}
                    </div>
                  </Card>

                  {/* Star Ratings */}
                  <Card className="p-6 glass-card">
                    <div className="flex items-center gap-2 mb-6">
                      <Star className="w-5 h-5 text-primary fill-current" />
                      <h3 className="text-lg">Star Ratings</h3>
                    </div>

                    <div className="space-y-3">
                      {[5, 4, 3, 2, 1].map((stars) => (
                        <div key={stars} className="flex items-center gap-4">
                          <div className="flex items-center gap-1 w-16">
                            <span className="text-sm font-medium">{stars}</span>
                            <Star className="w-3 h-3 fill-current text-primary" />
                          </div>
                          <Progress 
                            value={totalReviews > 0 ? (starDistribution[stars as keyof typeof starDistribution] / totalReviews) * 100 : 0}
                            className="flex-1 h-2"
                          />
                          <span className="text-sm text-muted-foreground w-12 text-right">
                            {starDistribution[stars as keyof typeof starDistribution]}
                          </span>
                        </div>
                      ))}
                    </div>
                  </Card>

                  {/* Detailed Ratings */}
                  <Card className="p-6 glass-card">
                    <div className="flex items-center gap-2 mb-6">
                      <Leaf className="w-5 h-5 text-primary fill-current" />
                      <h3 className="text-lg">Detailed Ratings</h3>
                    </div>

                    <div className="space-y-3">
                      {Object.entries(detailedRatings).map(([field, rating]) => (
                        <div key={field} className="flex items-center gap-4">
                          <div className="flex items-center gap-1 w-16">
                            <span className="text-sm font-medium">{field.charAt(0).toUpperCase() + field.slice(1)}</span>
                          </div>
                          <Progress 
                            value={rating !== null ? (rating / 5) * 100 : 0}
                            className="flex-1 h-2"
                          />
                          <span className="text-sm text-muted-foreground w-12 text-right">
                            {rating !== null ? rating.toFixed(1) : "N/A"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </Card>

                  {/* Best Used Preferences */}
                  <Card className="p-6 glass-card">
                    <div className="flex items-center gap-2 mb-6">
                      <Sun className="w-5 h-5 text-primary fill-current" />
                      <h3 className="text-lg">Best Used Preferences</h3>
                    </div>

                    <div className="space-y-3">
                      {Object.entries(bestUsedCounts).map(([time, count]) => (
                        <div key={time} className="flex items-center gap-4">
                          <div className="flex items-center gap-1 w-16">
                            <span className="text-sm font-medium">{time.charAt(0).toUpperCase() + time.slice(1)}</span>
                          </div>
                          <Progress 
                            value={totalReviews > 0 ? (count / totalReviews) * 100 : 0}
                            className="flex-1 h-2"
                          />
                          <span className="text-sm text-muted-foreground w-12 text-right">
                            {count}
                          </span>
                        </div>
                      ))}
                    </div>
                  </Card>

                  {/* Repeat Purchase Rate */}
                  <Card className="p-6 glass-card">
                    <div className="flex items-center gap-2 mb-6">
                      <Zap className="w-5 h-5 text-primary fill-current" />
                      <h3 className="text-lg">Repeat Purchase Rate</h3>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1 w-16">
                          <span className="text-sm font-medium">Rate</span>
                        </div>
                        <Progress 
                          value={repeatPurchaseRate !== null ? repeatPurchaseRate : 0}
                          className="flex-1 h-2"
                        />
                        <span className="text-sm text-muted-foreground w-12 text-right">
                          {repeatPurchaseRate !== null ? `${repeatPurchaseRate.toFixed(1)}%` : "N/A"}
                        </span>
                      </div>
                    </div>
                  </Card>
                </TabsContent>

                {/* Batches Tab */}
                <TabsContent value="batches" className="space-y-4">
                  {batches.length === 0 ? (
                    <Card className="p-12 glass-card text-center">
                      <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                      <h3 className="text-lg mb-2">No batches available</h3>
                      <p className="text-sm text-muted-foreground">
                        This product doesn't have any batches yet.
                      </p>
                    </Card>
                  ) : (
                    batches.map((batch, index) => (
                      <motion.div
                        key={batch.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <Card 
                          className="p-6 glass-card hover-lift cursor-pointer group"
                          onClick={() => onNavigate && onNavigate("batch-detail", batch.id)}
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div>
                              <h4 className="text-lg mb-1 group-hover:text-primary transition-colors">
                                Batch #{batch.batch_no}
                              </h4>
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Calendar className="w-4 h-4" />
                                <span>
                                  Received: {batch.received_at ? new Date(batch.received_at).toLocaleDateString() : "Unknown"}
                                </span>
                              </div>
                            </div>
                            <Badge 
                              variant={batch.status === "in_stock" ? "default" : "secondary"}
                              className={batch.status === "in_stock" ? "gradient-green text-white" : ""}
                            >
                              {batch.status.replace("_", " ")}
                            </Badge>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div className="p-3 rounded-lg bg-muted/30">
                              <p className="text-xs text-muted-foreground mb-1">Expiry Date</p>
                              <p className="text-sm font-medium">
                                {batch.expiry_date ? new Date(batch.expiry_date).toLocaleDateString() : "N/A"}
                              </p>
                            </div>
                            <div className="p-3 rounded-lg bg-muted/30">
                              <p className="text-xs text-muted-foreground mb-1">COA</p>
                              <p className="text-sm font-medium">
                                {batch.coa_url ? "Available" : "N/A"}
                              </p>
                            </div>
                          </div>
                        </Card>
                      </motion.div>
                    ))
                  )}
                </TabsContent>

                {/* Reviews Tab */}
                <TabsContent value="reviews" className="space-y-4">
                  {reviews.length === 0 ? (
                    <Card className="p-12 glass-card text-center">
                      <Star className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                      <h3 className="text-lg mb-2">No reviews yet</h3>
                      <p className="text-sm text-muted-foreground mb-6">
                        Be the first to review this product!
                      </p>
                      <Button 
                        onClick={() => onNavigate && onNavigate("submit-review", productId)}
                        className="gradient-green hover:opacity-90 transition-opacity"
                      >
                        Write a Review
                      </Button>
                    </Card>
                  ) : (
                    reviews.map((review, index) => (
                      <motion.div
                        key={review.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <Card 
                          className="p-6 glass-card hover-lift cursor-pointer group"
                          onClick={() => onNavigate && onNavigate("review-detail", review.id)}
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <div className="flex">
                                  {[...Array(5)].map((_, i) => (
                                    <Star
                                      key={i}
                                      className={`w-4 h-4 ${
                                        i < review.stars
                                          ? "fill-current text-primary"
                                          : "text-muted-foreground"
                                      }`}
                                    />
                                  ))}
                                </div>
                                <Badge className="text-xs">{getGradeFromStars(review.stars)}</Badge>
                              </div>
                              {review.title && (
                                <h4 className="text-lg mb-2 group-hover:text-primary transition-colors">
                                  {review.title}
                                </h4>
                              )}
                              {review.body && (
                                <p className="text-sm text-muted-foreground line-clamp-2">
                                  {review.body}
                                </p>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <span>By {review.author?.display_name || "Anonymous"}</span>
                            <span>{new Date(review.submitted_at).toLocaleDateString()}</span>
                          </div>
                        </Card>
                      </motion.div>
                    ))
                  )}
                </TabsContent>
              </Tabs>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Overall Grade Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Card className="p-8 glass-card text-center">
                <div className="mb-4">
                  <Award className="w-8 h-8 mx-auto text-primary" />
                </div>
                <h3 className="text-sm text-muted-foreground mb-4">Overall Grade</h3>
                <div className={`${getGradientClass(overallGrade)} w-32 h-32 rounded-3xl mx-auto flex items-center justify-center shadow-2xl mb-6`}>
                  <span className="text-5xl text-white">{overallGrade}</span>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-5 h-5 ${
                          i < Math.round(avgStars)
                            ? "fill-current text-primary"
                            : "text-muted-foreground"
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-2xl">{avgStars.toFixed(1)}/5.0</p>
                  <p className="text-sm text-muted-foreground">
                    Based on {reviewCount} {reviewCount === 1 ? "review" : "reviews"}
                  </p>
                </div>
              </Card>
            </motion.div>

            {/* Quick Actions */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1, duration: 0.5 }}
            >
              <Card className="p-6 glass-card">
                <h3 className="text-lg mb-4">Quick Actions</h3>
                <div className="space-y-3">
                  <Button 
                    className="w-full gradient-green hover:opacity-90 transition-opacity"
                    onClick={() => onNavigate && onNavigate("submit-review", productId)}
                  >
                    <Star className="w-4 h-4 mr-2" />
                    Write a Review
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                  >
                    <TrendingUp className="w-4 h-4 mr-2" />
                    View All Batches
                  </Button>
                </div>
              </Card>
            </motion.div>

            {/* Disclaimer */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <Card className="p-6 glass-card">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm mb-2">Medical Disclaimer</h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Reviews reflect individual patient experiences and should not be considered medical advice. Always consult with your healthcare provider before use.
                    </p>
                  </div>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}